<template>
  <div class="location-search">
    <div class="search-container">
      <input
        v-model="searchQuery"
        @input="onSearchInput"
        type="text"
        placeholder="Search location..."
        class="search-input"
      />
      <div v-if="isLoading" class="loading">Searching...</div>
      <div v-if="error" class="error">{{ error }}</div>
      <div v-if="showResults && locations.length > 0" class="results">
        <div
          v-for="(location, i) in locations"
          :key="i"
          @click="selectLocation(location)"
          class="location-item"
        >
          <div class="location-title">{{ location.title }}</div>
          <div class="location-subtitle" v-if="location.subtitle">{{ location.subtitle }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { initFirebase } from '../firebase'

const { functions } = initFirebase()

export default {
  name: 'LocationSearch',
  emits: ['location-selected'],
  setup(props, { emit }) {
    const searchQuery = ref('')
    const locations = ref([])
    const isLoading = ref(false)
    const error = ref(null)
    const showResults = ref(false)
    let searchTimeout = null

    const searchLocations = async (query) => {
      if (query.length < 2) {
        locations.value = []
        showResults.value = false
        return
      }

      isLoading.value = true
      error.value = null

      try {
        const searchFn = httpsCallable(functions, 'searchLocation')
        const result = await searchFn({ query, limit: 5 })
        const data = result.data

        if (data && data.success && Array.isArray(data.results)) {
          locations.value = data.results.map(item => ({
            title: item.title || '',
            subtitle: item.subtitle || '',
            latitude: item.latitude,
            longitude: item.longitude,
            zipcode: item.zipcode || '',
            address: item.address || null
          }))
        } else {
          locations.value = []
        }
        showResults.value = locations.value.length > 0
      } catch (err) {
        error.value = 'Error searching locations'
        console.error('Location search error:', err)
        locations.value = []
        showResults.value = false
      } finally {
        isLoading.value = false
      }
    }

    const onSearchInput = () => {
      if (searchTimeout) clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        searchLocations(searchQuery.value)
      }, 500)
    }

    const selectLocation = (location) => {
      const displayName = location.subtitle
        ? `${location.title}, ${location.subtitle}`
        : location.title
      emit('location-selected', {
        name: displayName,
        lat: location.latitude,
        lng: location.longitude
      })
      searchQuery.value = displayName
      showResults.value = false
    }

    watch(searchQuery, (newValue) => {
      if (newValue.length < 2) {
        showResults.value = false
      }
    })

    return {
      searchQuery,
      locations,
      isLoading,
      error,
      showResults,
      onSearchInput,
      selectLocation
    }
  }
}
</script>

<style scoped>
.location-search {
  width: 100%;
  max-width: none;
  margin: 0;
  position: relative;
}

.search-container {
  position: relative;
}

.search-input {
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 14px 16px;
  color: var(--text-primary, #fff);
  font-size: 15px;
  font-family: inherit;
  height: 48px;
  box-sizing: border-box;
  transition: all 0.2s ease;
  outline: none;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.search-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.3);
}

.results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(26, 27, 46, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  margin-top: 4px;
  max-height: 240px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.location-item {
  padding: 12px 14px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.location-item:last-child {
  border-bottom: none;
}

.location-item:hover {
  background: rgba(108, 99, 255, 0.08);
}

.location-title {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.location-subtitle {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.loading {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  font-size: 12px;
}

.error {
  color: #FF5252;
  font-size: 12px;
  margin-top: 6px;
}

@media (max-width: 768px) {
  .location-item {
    padding: 10px 12px;
  }
}
</style>
